import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import PanelLayout from "@/components/layout/PanelLayout";
import BottomPageNav from "@/components/nav/BottomPageNav";
import { Mail, ChevronRight, CheckCircle2, ArrowLeft, Sparkles } from "lucide-react";
import { AdSlotBanner } from "@/components/referrals/AdSlotBanner";
import BudgetAnalyticsTiles from "@/components/budget/BudgetAnalyticsTiles";
import WishlistsPlannedTile from "@/components/wishlist/WishlistsPlannedTile";
import ProfileCompletionBar from "@/components/settings/ProfileCompletionBar";

// QA-only "Back to QA menu" surface helpers. The label itself is now
// resolved via `t('nav.backToQAMenu')` on every render so that adding a
// new language only requires extending translations.js. The legacy map
// is kept as a safety fallback for any edge case where `t` is unavailable
// (e.g. unit tests rendering this component in isolation).
export const BACK_TO_QA_LABEL = {
  en: "Back to QA menu",
  es: "Volver al menú QA",
  zh: "返回 QA 菜单",
};
export const isQARole = (role) => role === "qa_manager" || role === "qa_tester";
// QA Manager has the admin sidebar; tester does not. Tester's "QA menu" is
// the evaluation panel (this dashboard).
export const qaBackTarget = (role) => (role === "qa_manager" ? "/admin" : "/app");

// POST-DEPLOY-FIX-04 (final) — staff-role gating for reward widgets is
// now handled inside the standalone `MyRewardsPage` (the public panel
// no longer hosts reward UI). The `STAFF_ROLE_KEYS` set that used to
// live here has moved with that responsibility.

const UserDashboard = () => {
  const { user, refresh } = useAuth();
  const { t, pickI18n, tQuestionnaireTitle } = useLang();
  const navigate = useNavigate();
  const [questionnaires, setQuestionnaires] = useState([]);
  // A19-P6.1.1 — sutil discovery dot on the "My referrals" entry link when
  // there's a campaign matching this user's profile/country. Reuses the
  // P6.1 endpoint; never blocks the dashboard if the call fails. Honors
  // a per-campaign localStorage dismiss flag.
  const [navBoost, setNavBoost] = useState(null);

  useEffect(() => {
    api.get("/questionnaires").then((r) => setQuestionnaires(r.data)).catch(() => {});
    api.get("/referrals/active-boost-for-me").then((r) => {
      const data = r.data;
      if (data?.active && data.campaign_id) {
        try {
          const dismissed = localStorage.getItem(`dismissed_boost_nav_badge_${data.campaign_id}`);
          if (!dismissed) setNavBoost(data);
        } catch { setNavBoost(data); }
      }
    }).catch(() => {});
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resendVerification = async () => {
    await api.post("/auth/resend-verification", { email: user.email });
  };

  return (
    <PanelLayout>
      <div className="space-y-6">
            {/* QA Manager / QA Tester accessing the public panel are here
                to evaluate assigned officials. Surface a high-contrast
                "Back to QA menu" link so they can return to their natural
                workspace (admin sidebar for managers, this same panel for
                testers — kept for consistency with the manager flow). */}
            {isQARole(user?.role) && qaBackTarget(user.role) !== "/app" && (
              <button
                type="button"
                onClick={() => navigate(qaBackTarget(user.role))}
                className="tsw-btn-primary text-sm self-start"
                data-testid="qa-back-to-menu"
              >
                <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("nav.backToQAMenu")}
              </button>
            )}
            <div>
              <div className="tsw-overline">{t("common.dashboard")}</div>
              <h1
                className="text-2xl font-bold text-slate-900 mt-1"
                data-testid="dashboard-page-title"
              >
                {t("user.welcome")} {user?.full_name?.split(" ")[0]}
              </h1>
            </div>

            {/* Referral boost badge — surfaced inline so the discovery
                signal isn't lost after the chip row was retired. The
                navigation entry for `Mis referidos` lives in the side
                menu now; this card highlights an active multiplier and
                lets the user dismiss it. */}
            {navBoost && (
              <Link
                to="/app/referrals"
                className="inline-flex items-center gap-2 self-start rounded-full bg-amber-50 border border-amber-300 text-amber-900 hover:bg-amber-100 px-3 py-1.5 text-xs font-semibold transition"
                data-testid="dashboard-nav-boost-badge"
                title={t("referrals.navBadge.tooltip", { m: `${navBoost.multiplier}`.replace(/\.0$/, "") })}
              >
                <Sparkles className="w-3 h-3" />
                ×{`${navBoost.multiplier}`.replace(/\.0$/, "")}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    try { localStorage.setItem(`dismissed_boost_nav_badge_${navBoost.campaign_id}`, "1"); } catch {/* no-op */}
                    setNavBoost(null);
                  }}
                  className="ms-1 text-amber-700 hover:text-amber-900 opacity-60 hover:opacity-100 transition"
                  data-testid="dashboard-nav-boost-dismiss"
                  aria-label={t("referrals.navBadge.dismiss")}
                  title={t("referrals.navBadge.dismiss")}
                >
                  ×
                </button>
              </Link>
            )}

            {/* P5.2 — Profile completion reminder. Renders ONLY when the
                profile is < 80% complete (Pepe Q1.3). The component
                short-circuits to null at >= 80%, so no extra guard here. */}
            <ProfileCompletionBar variant="dashboard" />

            {/* A20-P4 — Budget overview tile (read-only aggregates, per-user). */}
            <BudgetAnalyticsTiles />

            {/* A20-P9.1 — Wishlists with planned (calendar-linked) events. */}
            <WishlistsPlannedTile />

            {/* A19-P6.3 — Internal promotional slot for placement `dashboard_home`.
                Hidden when no slot matches or the engine returns active=false. */}
            <AdSlotBanner placement="dashboard_home" />

            {!user?.email_verified && (
              <div className="tsw-card p-5 border-amber-200 bg-amber-50 flex items-start gap-3" data-testid="verify-banner">
                <Mail className="w-5 h-5 text-amber-700 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-amber-900">{t("auth.verifyTitle")}</div>
                  <p className="text-amber-800 text-sm">{t("auth.verifyBody")}</p>
                  {/* Mock-mode QA helper: surface the verify URL so QA can
                      complete the flow without real email delivery. The
                      backend only returns this in EMAIL_MOCK_MODE; in
                      production this paragraph silently disappears. */}
                  {(() => {
                    const devUrl = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("tsw_dev_verify_url") : null;
                    if (!devUrl) return null;
                    return (
                      <div className="mt-2 text-xs text-amber-900 bg-amber-100 border border-amber-200 rounded px-2 py-1.5 break-all" data-testid="dev-verify-url">
                        <span className="font-bold">MOCK MODE · </span>
                        <a href={devUrl} className="underline decoration-dotted">{devUrl}</a>
                      </div>
                    );
                  })()}
                </div>
                <button onClick={resendVerification} className="tsw-btn-secondary text-sm" data-testid="resend-verify">{t("auth.resend")}</button>
              </div>
            )}

            <div>
              <h2 className="font-display font-semibold text-xl text-slate-900 mb-3">{t("user.yourQuestionnaires")}</h2>
              {questionnaires.length === 0 && <p className="text-slate-500 text-sm">{t("user.noQuestionnaires")}</p>}
              <div className="grid sm:grid-cols-2 gap-4">
                {questionnaires.map((q) => {
                  // Gate questionnaire access on email verification. Until
                  // the user verifies, the card stays visible (so they can
                  // see what's coming) but the Open action is disabled and
                  // a small hint nudges them to verify first. Avoids
                  // hiding-without-explanation; works even though the
                  // backend currently does not enforce verified-only on
                  // /questionnaires (defense-in-UX, not security).
                  const verifyGated = !user?.email_verified;
                  const verifyHint = t("phase2.verifyToOpen");
                  const cardInner = (
                    <>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-display font-semibold text-slate-900 text-base leading-snug">
                            {tQuestionnaireTitle(q)}
                          </div>
                          <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                            {pickI18n(q, "description") || q.description_en}
                          </p>
                        </div>
                        {q.completed && <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />}
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">{q.questions?.length || 0} {t("phase2.questionsCount")}</span>
                        {verifyGated ? (
                          <span className="text-amber-700 font-medium inline-flex items-center gap-1" data-testid={`questionnaire-locked-${q.id}`}>
                            {t("phase2.lockedBadge")}
                          </span>
                        ) : (
                          <span className="text-emerald-700 font-medium inline-flex items-center gap-1">
                            {q.completed ? t("user.completed") : t("user.open")} <ChevronRight className="w-4 h-4 rtl:rotate-180" />
                          </span>
                        )}
                      </div>
                      {verifyGated && (
                        <div className="text-xs text-amber-700 -mt-1">{verifyHint}</div>
                      )}
                    </>
                  );
                  if (verifyGated) {
                    return (
                      <div
                        key={q.id}
                        className="tsw-card p-5 flex flex-col gap-3 opacity-60 cursor-not-allowed select-none"
                        title={verifyHint}
                        aria-disabled="true"
                        data-testid={`questionnaire-card-${q.id}`}
                      >
                        {cardInner}
                      </div>
                    );
                  }
                  return (
                    <Link to={`/questionnaire/${q.id}`} key={q.id} className="tsw-card tsw-card-hover p-5 flex flex-col gap-3" data-testid={`questionnaire-card-${q.id}`}>
                      {cardInner}
                    </Link>
                  );
                })}
              </div>
            </div>
        {/* RACKET-LAB-PLAYER-PREVIEW-UX-1 (Feb 2026) — player-side Racket Lab
            preview block. After DASHBOARD-SIDEMENU-PROFILES-1-FIX2 each
            sub-tab (Mis encordados / Mi equipación / Reserva / Mi QR)
            lives on its own dedicated route reachable from the side
            menu, so this duplicate block is no longer rendered. */}
        <BottomPageNav />
      </div>
    </PanelLayout>
  );
};

export default UserDashboard;
